import { useCallback } from 'react';
import { graphql, fetchQuery, useRelayEnvironment } from 'react-relay';
import { fireErrorAnalytics } from '@jira/platform__error-reporting/src/fireErrorAnalytics.tsx';
import type { HydratedValues, HydratedValue } from '@atlaskit/jql-editor';
import { fg } from '@atlassian/jira-feature-gating';
import { useTenantContext } from '@atlassian/jira-tenant-context-controller/src/components/tenant-context/index.tsx';
import { PACKAGE_NAME, TEAM_NAME } from '../../common/constants.tsx';
import type {
	mapHydratedValuesFromDataQuery,
	mapHydratedValuesFromDataQuery$data,
	JiraJqlViewContext,
	JiraJqlScopeInput,
} from './__generated__/mapHydratedValuesFromDataQuery.graphql';
import { EPIC_LINK_SEARCHER_KEY, PARENT_LINK_SEARCHER_KEY } from './constants.tsx';

export const mapHydratedValuesFromData = (
	response?: mapHydratedValuesFromDataQuery$data,
): HydratedValues => {
	if (!response?.jira?.jqlBuilder?.hydrateJqlQuery?.fields) {
		return {};
	}

	const hydratedValues: HydratedValues = {};

	response.jira.jqlBuilder.hydrateJqlQuery.fields.forEach((field) => {
		if (field.values) {
			const fieldValues: Array<HydratedValue> = [];
			field.values.forEach((valueResult) => {
				if (valueResult?.values) {
					valueResult.values.forEach((value) => {
						if (value?.__typename === 'JiraJqlUserFieldValue') {
							fieldValues.push({
								type: 'user',
								id: value.jqlTerm,
								name: value.displayName,
								avatarUrl: value.user?.picture ?? '',
							});
						}
						if (value?.__typename === 'JiraJqlTeamFieldValue' && fg('jira_update_jql_teams')) {
							fieldValues.push({
								type: 'team',
								id: value.jqlTerm,
								name: value?.team?.name || value.displayName,
								avatarUrl: value.team?.avatar?.small ?? '',
							});
						}
					});
				}
			});

			if (field.field && field.field.searchTemplate) {
				const fieldName = field.field.jqlTerm;
				if (EPIC_LINK_SEARCHER_KEY === field.field.searchTemplate.key) {
					fieldValues.push({
						type: 'deprecated-field',
						id: fieldName,
						deprecatedSearcherKey: field.field.searchTemplate.key,
					});
				}
			}

			if (field.field && field.field.searchTemplate) {
				const fieldName = field.field.jqlTerm;
				if (PARENT_LINK_SEARCHER_KEY === field.field.searchTemplate.key) {
					fieldValues.push({
						type: 'deprecated-field',
						id: fieldName,
						deprecatedSearcherKey: field.field.searchTemplate.key,
					});
				}
			}

			if (fieldValues.length > 0 && field.field) {
				const fieldName = field.field.jqlTerm;
				hydratedValues[fieldName] = fieldValues;
			}
		}
	});

	return hydratedValues;
};

export const useOnHydrate = (
	viewContext: JiraJqlViewContext | null,
	scope: JiraJqlScopeInput | null,
) => {
	const environment = useRelayEnvironment();
	const { cloudId } = useTenantContext();
	return useCallback<(arg1: string) => Promise<HydratedValues>>(
		async (jql: string) => {
			try {
				const data = await fetchQuery<mapHydratedValuesFromDataQuery>(
					environment,
					graphql`
						query mapHydratedValuesFromDataQuery(
							$jql: String!
							$cloudId: ID!
							$viewContext: JiraJqlViewContext
							$scope: JiraJqlScopeInput
							$includeTeamField: Boolean!
						) {
							jira {
								jqlBuilder(cloudId: $cloudId) {
									hydrateJqlQuery(query: $jql, viewContext: $viewContext, scope: $scope) {
										... on JiraJqlHydratedQuery {
											fields {
												... on JiraJqlQueryHydratedField {
													field {
														jqlTerm
														searchTemplate {
															key
														}
													}
													values {
														... on JiraJqlQueryHydratedValue {
															values {
																jqlTerm
																displayName
																__typename
																... on JiraJqlUserFieldValue {
																	user {
																		picture
																	}
																}
																... on JiraJqlTeamFieldValue @include(if: $includeTeamField) {
																	team {
																		name
																		avatar {
																			small
																		}
																	}
																}
															}
														}
													}
												}
											}
										}
									}
								}
							}
						}
					`,
					{
						jql,
						cloudId,
						viewContext,
						scope,
						includeTeamField: fg('jira_update_jql_teams'),
					},
					{
						networkCacheConfig: {
							metadata: { META_SLOW_ENDPOINT: true },
						},
					},
				).toPromise();
				return mapHydratedValuesFromData(data);
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
			} catch (error: any) {
				fireErrorAnalytics({
					error,
					meta: {
						id: 'useOnHydrateRelay',
						packageName: PACKAGE_NAME,
						teamName: TEAM_NAME,
					},
				});
				throw error;
			}
		},
		[cloudId, environment, viewContext, scope],
	);
};
